import { Mic, FileText, Image as ImageIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ChatMessage } from "./types";

interface Props {
  isOwn: boolean;
  target: NonNullable<ChatMessage["replyToMessage"]>;
  onTap?: () => void;
  currentUserId: string;
}

/**
 * WhatsApp/iMessage-style inline reply quote: a small tile inside the
 * top of the reply bubble showing who is being replied to and a
 * 1-line preview of the original (with attachment thumbnail when applicable).
 */
export function ReplyQuote({ isOwn, target, onTap, currentUserId }: Props) {
  const senderName =
    target.sender.id === currentUserId ? "You" : target.sender.firstName;

  const firstAttachment = target.attachments?.[0];
  const isImage = firstAttachment?.kind === "image" && firstAttachment.url;

  const preview = (() => {
    if (target.deletedForEveryone) return "Deleted message";
    if (target.body && target.body.length > 0) return target.body;
    if (firstAttachment?.kind === "image") return "Photo";
    if (firstAttachment?.kind === "voice") {
      const d = firstAttachment.durationSeconds ?? 0;
      const m = Math.floor(d / 60);
      const r = Math.floor(d % 60);
      return `Voice note · ${m}:${r.toString().padStart(2, "0")}`;
    }
    if (firstAttachment?.kind === "pdf") return firstAttachment.filename ?? "PDF";
    if (firstAttachment) return firstAttachment.filename ?? "Attachment";
    return "Message";
  })();

  const Icon = (() => {
    if (firstAttachment?.kind === "voice") return Mic;
    if (firstAttachment?.kind === "image") return ImageIcon;
    if (firstAttachment) return FileText;
    return null;
  })();

  return (
    <button
      type="button"
      onClick={onTap}
      className={cn(
        "relative flex w-full items-stretch gap-2 overflow-hidden rounded-lg pl-2 pr-2 py-1.5 text-left transition-colors",
        isOwn
          ? "bg-background/10 hover:bg-background/20"
          : "bg-foreground/[0.06] hover:bg-foreground/10",
      )}
      aria-label={`Reply to ${senderName}: ${preview}`}
    >
      <span className="absolute inset-y-0 left-0 w-[3px] rounded-l-lg bg-primary" />
      <div className="min-w-0 flex-1 pl-1.5">
        <div className="text-[12px] font-semibold leading-tight text-primary">
          {senderName}
        </div>
        <p
          className={cn(
            "mt-0.5 line-clamp-1 flex items-center gap-1 text-[12px] leading-4",
            target.deletedForEveryone && "italic",
            isOwn ? "text-background/75" : "text-muted-foreground",
          )}
        >
          {Icon && !isImage && <Icon className="size-3 shrink-0" />}
          <span className="truncate">{preview}</span>
        </p>
      </div>
      {isImage && firstAttachment?.url && (
        <img
          src={firstAttachment.url}
          alt=""
          aria-hidden="true"
          className="size-10 shrink-0 rounded object-cover"
        />
      )}
    </button>
  );
}
